
  import { defineComponent, ref, computed } from 'vue';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useI18n } from 'vue-i18n/index';
  import CustomDatePicker from '@/components/search/CustomDatePicker.vue';

  type Change = 'increase' | 'decrease' | 'noChange' | '';

  interface IreItem {
    type: 'residential' | 'investment' | 'build' | 'fund' | 'total';
    count: number;
    countChange: Change; // API sends "" sometimes
    countPercentage: number;
  }

  export default defineComponent({
    name: 'IreStatsWidget',
    props: {
      widgetClasses: { type: String, default: 'card-xxl-stretch mb-xl-8' },
    },
    components: {
      CustomDatePicker,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const loading = ref(true);
      const selectedPeriod = ref('last-7-days');
      const customDateFrom = ref('');
      const customDateTo = ref('');
      const showDatePicker = ref(false);
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      await store.dispatch(Actions.GET_IRE_SUMMARY_DASHBOARD, {
        dateRange: selectedPeriod.value,
        showTrend: false,
      });
      loading.value = false;

      const payload = computed(() => store.getters.ireSummaryDashboard);

      const byType = (t: IreItem['type']) =>
        payload.value?.data?.find((x: IreItem) => x.type === t) || null;

      const investmentData = computed<IreItem | null>(() =>
        byType('investment')
      );
      const residentialData = computed<IreItem | null>(() =>
        byType('residential')
      );
      const buildData = computed<IreItem | null>(() => byType('build'));
      const fundData = computed<IreItem | null>(() => byType('fund'));
      const totalData = computed<IreItem | null>(() => byType('total'));

      const showCustomDatePicker = () => {
        showDatePicker.value = true;
        selectedPeriod.value = 'custom';
      };

      const applyCustomDates = async () => {
        if (!customDateFrom.value || !customDateTo.value) {
          alert('Please select both dates');
          return;
        }
        showDatePicker.value = false;
        await selectPeriod('custom');
      };

      const selectPeriod = async (period: string) => {
        selectedPeriod.value = period;
        loading.value = true;
        const obj = {
          dateRange: period,
          showTrend: false,
          dateFrom: '',
          dateTo: '',
        };

        if (period === 'custom' && customDateFrom.value && customDateTo.value) {
          obj.dateFrom = customDateFrom.value;
          obj.dateTo = customDateTo.value;
        }

        await store.dispatch(Actions.GET_IRE_SUMMARY_DASHBOARD, obj);
        loading.value = false;
      };

      const periodLabel = computed(() =>
        selectedPeriod.value === 'last-30-days'
          ? 'Last 30 Days'
          : selectedPeriod.value === 'last-7-days'
          ? 'Last 7 Days'
          : 'Today'
      );

      const trendSymbol = (c: Change) =>
        c === 'increase' ? '↑' : c === 'decrease' ? '↓' : '↔';
      const trendClass = (c: Change) =>
        c === 'increase' ? 'up' : c === 'decrease' ? 'down' : 'flat';
      const formatCount = (n: number) => n.toLocaleString();

      const icon = (t: string) => {
        const map: Record<string, string> = {
          investment: '/media/icons/dash/rent-investment.png',
          residential: '/media/icons/dash/rent-residential.png',
          build: '/media/icons/dash/build.png',
          fund: '/media/icons/dash/fund.png',
        };
        return map[t] || '/media/icons/dash/default.png';
      };

      return {
        loading,
        periodLabel,
        selectPeriod,
        investmentData,
        residentialData,
        buildData,
        fundData,
        totalData,
        trendSymbol,
        trendClass,
        formatCount,
        icon,
        selectedPeriod,
        translate,
        showDatePicker,
        customDateFrom,
        customDateTo,
        showCustomDatePicker,
        applyCustomDates,
      };
    },
  });
